import React from 'react';
import Modal from 'src/UI/Modal/Modal';
import cls from './SubscriptionExpiredModal.module.scss';
import { ReactComponent as UsersCountIcon } from 'src/assets/img/icons/users-count-icon.svg';
import { ReactComponent as BagIcon } from 'src/assets/img/icons/bag-icon.svg';
import { ReactComponent as InfinityIcon } from 'src/assets/img/icons/infinity.svg';
import { ReactComponent as InfoIcon } from 'src/assets/img/icons/info-gray-circle-icon.svg';
import { SubscriptionType } from 'src/models/IAuth';
import dayjs from 'dayjs';
import { PAGES_ENUM } from 'src/routes';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import selectors from 'src/redux/selectors';
import { useLogoutMutation } from 'src/services/Auth';
import { clearStore } from 'src/redux/Auth';
import {toast} from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useTypedDispatch } from 'src/hooks/useTypedDispatch';
import {useTranslation} from 'react-i18next';

interface ISubscriptionExpiredModal {
  t: any;
  title: string;
  open: boolean;
  close: () => void;
  hideCloseIcon: boolean;
  isCanCloseModal: boolean;
  expired: boolean
  subscription?: {
    id?: number;
    start_date?: string;
    end_date?: string;
    SubscriptionType?: SubscriptionType;
  };
}

const SubscriptionExpiredModal = ({
  open,
  close,
  subscription,
  t,
  title,
  hideCloseIcon,
  isCanCloseModal,
  expired,
}: ISubscriptionExpiredModal) => {
  const [logout] = useLogoutMutation({});
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();
  const { t: tOut } = useTranslation('translation', { keyPrefix: 'user-dropdown' });

  const isUnlimitedUsers = subscription?.SubscriptionType?.full_user;
  const isUnlimitedOrders = subscription?.SubscriptionType?.full_order;
  const userData = useTypedSelector(selectors.getUser);

  const daysPassed = dayjs().diff(dayjs(subscription?.end_date), 'days');

  const logoutHandler = async () => {
    await logout({})
        .unwrap()
        .then(() => {
          dispatch(clearStore());
          close();
          return navigate(PAGES_ENUM.SIGN_IN);
        })
        .catch((e) => toast.error(e?.data?.message || 'logout fail'));
  };

  return (
    <Modal
      width="830px"
      close={close}
      open={open}
      title={title}
      bodyClassName={cls['sub-expired-modal-body']}
      hideCloseIcon={hideCloseIcon}
      isCanCloseModal={isCanCloseModal}
    >
      <div className={cls['sub-expired-modal-wrapper']}>
        <div className={cls['sub-expired-modal-content']}>
          <p className={cls['sub-expired-modal-title']}>
            {subscription?.SubscriptionType?.name}
          </p>
          <div className={cls['sub-expired-modal-price-box']}>
            <p className={cls['sub-expired-modal-price']}>
              {subscription?.SubscriptionType?.price}
            </p>
            <div className={cls['sub-expired-modal-price-wrapper']}>
              <p className={cls['sub-expired-modal-currency']}>USD</p>
              <p
                className={`${cls['sub-expired-modal-description']} ${cls['sub-expired-modal-description-duration']}`}
              >
                {t('month')}
              </p>
            </div>
          </div>
          {/*{expired ? (*/}
          {/*    <p className={`${cls['sub-expired-modal-description']}`}>*/}
          {/*      {t('subscription-duration', { value: 6 })}*/}
          {/*    </p>*/}
          {/*) : (<></>) }*/}
          <div className={cls['sub-expired-modal-tariffs-box']}>
            <div className={cls['sub-expired-modal-tariffs-item']}>
              <div className={cls['sub-expired-modal-tariffs-item-label']}>
                <UsersCountIcon className="icon--24" />
                <p
                  className={cls['sub-expired-modal-tariffs-item-label-title']}
                >
                  {t('users')}
                </p>
              </div>
              <p className={cls['sub-expired-modal-tariffs-item-value']}>
                {isUnlimitedUsers ? (
                  <InfinityIcon />
                ) : (
                  subscription?.SubscriptionType?.count_user
                )}
              </p>
            </div>
            <div className={cls['sub-expired-modal-tariffs-item']}>
              <div className={cls['sub-expired-modal-tariffs-item-label']}>
                <BagIcon className="icon--24" />
                <p
                  className={cls['sub-expired-modal-tariffs-item-label-title']}
                >
                  {t('orders')}
                </p>
              </div>
              <p className={cls['sub-expired-modal-tariffs-item-value']}>
                {isUnlimitedOrders ? (
                  <InfinityIcon />
                ) : (
                  subscription?.SubscriptionType?.count_order
                )}
              </p>
            </div>
          </div>
          <div className={cls['sub-expired-modal-agreement-wrap']}>
            <p className={cls['sub-expired-modal-agreement']}>
              {`${t('date-expired-subscription.active-until', { value: dayjs(subscription?.end_date).format('YYYY.MM.DD') })}`}
              {expired ? (
                  <>
                    {' ('}{t('date-expired-subscription.expired')}
                    <span className={cls['selected-text']}>
                      {`${t('date-expired-subscription.expired-days', { count: daysPassed, value: daysPassed })}`}
                    </span>
                    {' )'}
                  </>
              ) : (<></>)}
            </p>
          </div>
        </div>

        <div className={cls['sub-expired-modal-table']}>
          <div className={cls['sub-expired-table-content']}>
            <div className={cls['sub-expired-modal-agreement-box']}>
              <InfoIcon className="icon--16 flex-shrink-0" />
              <p className={cls['sub-expired-modal-agreement']}>
                {t('api-order-records')}
                <br />
                <span className={cls['selected-text']}>
                  {dayjs(subscription?.end_date)
                    .add(7, 'days')
                    .format('YYYY.MM.DD')}
                </span>
              </p>
            </div>
            <p
              className={`${cls['indent']} ${cls['sub-expired-modal-agreement']}`}
            >
              {t('crm-activation-condition.if-more-than')}{' '}
              <span className={cls['selected-text']}>
                {t('crm-activation-condition.days-have-passed')}
              </span>
              {', '}
              {t('crm-activation-condition.pay-for-reactivated-crm')}
            </p>
          </div>

          {userData?.is_superuser ? (
              <div className={cls['sub-expired-table-buttons-box']}>
                <button
                    className={cls['sub-expired-table-btn-change-plan']}
                    onClick={() => {
                      close();
                      window.location.href = PAGES_ENUM.SUBSCRIPTIONS;
                    }}
                >
                  {t('change-plan-btn')}
                </button>
                <button
                    className={cls['sub-expired-table-btn-update-sub']}
                    onClick={() => {
                      close();
                      window.location.href = PAGES_ENUM.SUBSCRIPTIONS;
                    }}
                >
                  {t('update-subscription-btn')}
                </button>
              </div>
          ) : (
              <div className={cls['sub-expired-table-buttons-box']}>
                <button
                    className={cls['sub-expired-table-btn-update-sub']}
                    onClick={() => {
                      logoutHandler();
                    }}
                >
                  {tOut('quit')}
                </button>
              </div>
          )}

        </div>
      </div>
    </Modal>
  );
};

export default SubscriptionExpiredModal;
